import type { IdentityRuntimeState } from './session-state';

/* docs/decisions/2026-10-06-authority-unavailable-is-not-invalid.md: while
 * the Panel shell watches, a session check that met a server or network
 * outage (a retryable 503 from the staff verifier included) keeps the
 * session and the closed Panel, and checks again by itself after 2, 4, 8,
 * 16 and then every 30 seconds, only while the tab is visible and online.
 * It repeats only the check that failed (the current-session read or the
 * same-key renewal), never a logout, and grants nothing: the Panel reopens
 * only on the server's own valid answer, and a verified 401 still expires. */
/** Automatic session re-checks after an outage: quick at first, then every 30 s. */
const SESSION_RECOVERY_DELAYS_MS = [2_000, 4_000, 8_000, 16_000, 30_000] as const;

/** The session check to repeat by itself, or null when only the person can act. */
function automaticSessionRecovery(state: IdentityRuntimeState): 'hydrate' | 'renew' | null {
  const failure = state.kind === 'unavailable' ? state
    : state.kind === 'credential_required' && state.status === 'unavailable' ? state : null;
  if (!failure || failure.operation === 'logout') return null;
  return failure.reason === 'server' || failure.reason === 'network' ? failure.operation : null;
}

/** A hidden or offline tab checks nothing by itself. */
const foregroundAndOnline = (): boolean =>
  (typeof document === 'undefined' || document.visibilityState !== 'hidden')
  && (typeof navigator === 'undefined' || navigator.onLine !== false);

export class SessionRecovery {
  private watchers = 0;
  private attempt = 0;
  private timer: ReturnType<typeof globalThis.setTimeout> | null = null;

  constructor(private readonly host: Readonly<{
    state: () => IdentityRuntimeState;
    renew: () => Promise<void>;
    rehydrate: () => Promise<void>;
  }>) {}

  watch = (): (() => void) => {
    this.watchers += 1;
    this.schedule();
    let watching = true;
    return () => {
      if (!watching) return;
      watching = false;
      this.watchers -= 1;
      this.schedule();
    };
  };

  /** Re-arms the one recovery timer for the current state; every commit calls it. */
  schedule(): void {
    if (this.timer !== null) globalThis.clearTimeout(this.timer);
    this.timer = null;
    const state = this.host.state();
    if (state.kind === 'ready') this.attempt = 0;
    const operation = automaticSessionRecovery(state);
    if (!operation || this.watchers === 0) return;
    const delay = SESSION_RECOVERY_DELAYS_MS[Math.min(this.attempt, SESSION_RECOVERY_DELAYS_MS.length - 1)];
    this.timer = globalThis.setTimeout(() => {
      this.timer = null;
      if (!foregroundAndOnline()) return;
      this.attempt += 1;
      void (operation === 'renew' ? this.host.renew() : this.host.rehydrate())
        // A check that settled without a new state still schedules the next one.
        .finally(() => { if (this.host.state() === state) this.schedule(); });
    }, delay);
  }
}
